import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ArticleDetails,
  FailedPosition,
  PositionStatusCounts,
  ProjectFailureInfo,
  ProjectInfo,
  SapDetails,
} from '@schueco/lca-search/domain-search';
import { Observable } from 'rxjs';

import { LCA_SEARCH_API_CONFIGURATION } from './lca-search-api.configuration';

@Injectable({ providedIn: 'root' })
export class LcaSearchApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(LCA_SEARCH_API_CONFIGURATION);

  getProjectById(projectId: string): Observable<ProjectInfo> {
    return this.http.get<ProjectInfo>(`${this.config.projectServiceUrl}/${projectId}`);
  }

  getProjectStatusCounts(): Observable<Record<string, number>> {
    return this.http.get<Record<string, number>>(`${this.config.projectServiceUrl}/status-counts`);
  }

  getProjectsWithFailures(): Observable<ProjectFailureInfo[]> {
    return this.http.get<ProjectFailureInfo[]>(`${this.config.projectServiceUrl}/failures`);
  }

  getPositionStatusCounts(): Observable<PositionStatusCounts> {
    return this.http.get<PositionStatusCounts>(`${this.config.positionServiceUrl}/position-info/status-counts`);
  }

  getFailedPositions(): Observable<FailedPosition[]> {
    return this.http.get<FailedPosition[]>(`${this.config.positionServiceUrl}/position-info/failed`);
  }

  getSapDetails(positionId: string): Observable<SapDetails> {
    return this.http.get<SapDetails>(`${this.config.positionServiceUrl}/sap-list/${positionId}`);
  }

  retryPosition(positionId: string): Observable<unknown> {
    return this.http.get(`${this.config.positionServiceUrl}/retry/${positionId}`);
  }

  deletePosition(positionId: string): Observable<unknown> {
    return this.http.delete(`${this.config.positionServiceUrl}/delete/${positionId}`);
  }

  /**
   * Downloads unrounded LCA calculation results as an Excel workbook.
   * `externalRef` is the EPDMS external position reference (same id used as `positionId` in LCA Control).
   */
  exportPositionLcaResults(externalRef: string): Observable<Blob> {
    const encodedRef = encodeURIComponent(externalRef);
    return this.http.get(`${this.config.adminApiBaseUrl}/positions/${encodedRef}/lca-results`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  }

  getArticleById(articleId: string): Observable<ArticleDetails> {
    return this.http.get<ArticleDetails>(`${this.config.articleServiceUrl}/${articleId}`);
  }
}
